import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime, humanizeDates } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Briefcase, CheckCircle2, User, ArrowLeft, MapPin, Phone, Clock, Camera,
  Truck, PlayCircle, Flag, AlertTriangle, PenLine, LogOut, ChevronRight, Mic, Languages,
} from 'lucide-react';
import { PRIORITY_SLA, priorityDotClass } from '@/components/wom/priority';

const ACTIVE_DONE = ['Awaiting Administrative Closeout', 'Completed', 'Closed by Admin', 'Closed', 'Cancelled', 'Work Completed'];
const fmt = (d, wt) => (d ? (wt ? (_fmtDateTime(d) || '—') : (_fmtDate(d) || '—')) : '—');

// ---- Bilingual helpers (EN/ES). Technician choices below remain English canonical
//      values for the backend; only the on-screen label is localized. ----
const tr = (lang, en, es) => (lang === 'es' ? es : en);
const STATUS_ES = {
  'Open - Unassigned': 'Abierta - Sin asignar',
  'Assigned': 'Asignada',
  'Scheduled': 'Programada',
  'Dispatched': 'Despachada',
  'En Route': 'En camino',
  'In Progress': 'En progreso',
  'Awaiting Authorization': 'Esperando autorización',
  'Awaiting Administrative Closeout': 'Esperando cierre administrativo',
  'Completed': 'Completada',
  'Closed by Admin': 'Cerrada por administración',
  'Work Completed': 'Trabajo completado',
};
const statusLabel = (lang, s) => (lang === 'es' ? (STATUS_ES[s] || s) : s);
const COMPLETION_STATUSES = [
  { v: 'Completed', en: 'Completed', es: 'Completado' },
  { v: 'Partially Completed', en: 'Partially Completed', es: 'Parcialmente completado' },
  { v: 'Unable to Complete', en: 'Unable to Complete', es: 'No se pudo completar' },
  { v: 'Follow-Up Required', en: 'Follow-Up Required', es: 'Requiere seguimiento' },
];
const VERIFY_METHODS = [
  { v: 'customer_signature', en: 'Customer signature', es: 'Firma del cliente' },
  { v: 'customer_rep_in_person', en: 'Customer representative — in person', es: 'Representante del cliente — en persona' },
  { v: 'customer_email', en: 'Customer email confirmation', es: 'Confirmación por correo del cliente' },
  { v: 'customer_phone', en: 'Customer telephone confirmation', es: 'Confirmación telefónica del cliente' },
];

const PriorityTag = ({ p }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold text-white ${p ? priorityDotClass(p) : 'bg-slate-500'}`}>
    {p || '—'} {PRIORITY_SLA[p] ? `· ${PRIORITY_SLA[p]}` : ''}
  </span>
);

// ---- Voice-to-text (Web Speech API). Dictates in the selected language and
//      appends to the current field value; silently hidden if unsupported. ----
const MicButton = ({ lang, onAppend, testid }) => {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  if (!SR) return null;
  const toggle = () => {
    if (listening) { try { recRef.current?.stop(); } catch { /* noop */ } return; }
    try {
      const rec = new SR();
      rec.lang = lang === 'es' ? 'es-ES' : 'en-US';
      rec.interimResults = false;
      rec.continuous = false;
      rec.onresult = (e) => {
        const txt = Array.from(e.results).map((r) => r[0].transcript).join(' ').trim();
        if (txt) onAppend(txt);
      };
      rec.onerror = () => { setListening(false); toast.error(tr(lang, 'Voice input unavailable', 'Entrada de voz no disponible')); };
      rec.onend = () => setListening(false);
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch { setListening(false); }
  };
  return (
    <button type="button" onClick={toggle} data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${listening ? 'border-red-400 text-red-600 animate-pulse' : 'border-slate-300 text-slate-500'}`}>
      <Mic size={13} /> {listening ? tr(lang, 'Listening…', 'Escuchando…') : tr(lang, 'Dictate', 'Dictar')}
    </button>
  );
};

// ---- Signature pad (touch/mouse) ----
const SignaturePad = ({ onChange, lang }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const c = ref.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext('2d'); const p = pos(e); c.lineTo(p.x, p.y); c.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL('image/png')); };
  const clear = () => { const c = ref.current.getContext('2d'); c.clearRect(0, 0, ref.current.width, ref.current.height); onChange(''); };
  useEffect(() => { const c = ref.current.getContext('2d'); c.strokeStyle = '#0E2A47'; c.lineWidth = 2; c.lineCap = 'round'; }, []);
  return (
    <div>
      <canvas ref={ref} width={640} height={160} data-testid="signature-canvas"
        className="w-full rounded-md border border-gray-300 bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} className="mt-1 text-xs text-slate-500 underline" data-testid="signature-clear">{tr(lang, 'Clear', 'Borrar')}</button>
    </div>
  );
};

// ---- Photo uploader ----
const PhotoRow = ({ woId, kind, photos, onChange, label, lang }) => {
  const { token } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
      const r = await axios.post(`${API}/work-orders/${woId}/photos`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onChange([...(photos || []), r.data.photo_id]);
      toast.success(tr(lang, 'Photo added', 'Foto agregada'));
    } catch (err) { toast.error(tr(lang, 'Upload failed', 'Error al subir')); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {(photos || []).map((id) => (
          <img key={id} src={`${API}/work-orders/photos/${id}`} alt="" className="h-16 w-16 rounded object-cover border" />
        ))}
      </div>
      <label className="inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-slate-600 cursor-pointer" data-testid={`photo-add-${kind}`}>
        <Camera size={16} /> {busy ? tr(lang, 'Uploading…', 'Subiendo…') : label}
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} />
      </label>
    </div>
  );
};

const StatusChip = ({ s, lang }) => (
  <span className="rounded-full bg-[#0E2A47] text-white px-2.5 py-0.5 text-xs font-semibold">{statusLabel(lang, s)}</span>
);

// ---- Detail with field status flow ----
const FieldDetail = ({ woId, onBack, onChanged, lang }) => {
  const { token, user } = useContext(AuthContext);
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [wo, setWo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [work, setWork] = useState({ work_performed: '', completion_notes: '', completion_status: 'Completed', follow_up_explanation: '', rep_name: '', rep_title: '', rep_company: '', rep_signature: '', no_rep_available: false, verification_method: 'customer_signature', feedback_courtesy: null, feedback_communication: null, feedback_resolution: null, feedback_comments: '' });
  const [conditionNotes, setConditionNotes] = useState('');
  const [auth, setAuth] = useState({ condition: '', recommended: '', estimate: '' });
  const [showAuth, setShowAuth] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [matStatus, setMatStatus] = useState('');

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/work-orders/${woId}`, H);
    const d = r.data;
    setWo(d); setBeforePhotos(d.before_photos || []); setAfterPhotos(d.after_photos || []);
    setMaterials(d.materials || []); setMatStatus(d.materials_status || '');
    setConditionNotes(d.initial_condition_notes || '');
    setWork((w) => ({
      ...w,
      work_performed: d.work_performed ?? w.work_performed ?? '',
      feedback_comments: d.feedback_comments ?? w.feedback_comments ?? '',
      completion_notes: d.completion_notes ?? w.completion_notes ?? '',
    }));
  }, [woId]);
  useEffect(() => { load(); }, [load]);

  const addMat = () => setMaterials((r) => [...r, { description: '', category: '', quantity: '', unit_cost: '', purchase_date: '', vendor: '', notes: '', receipt_photo_id: null, material_photo_id: null }]);
  const updMat = (i, k, v) => setMaterials((r) => r.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const rmMat = (i) => setMaterials((r) => r.filter((_, idx) => idx !== i));
  const matPhoto = async (i, kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
    try { const r = await axios.post(`${API}/work-orders/${woId}/photos`, fd, H);
      updMat(i, kind === 'receipt' ? 'receipt_photo_id' : 'material_photo_id', r.data.photo_id); toast.success(tr(lang, 'Photo attached', 'Foto adjuntada')); }
    catch { toast.error(tr(lang, 'Upload failed', 'Error al subir')); }
  };
  const cleanMats = () => materials.filter((m) => m.description).map((m) => ({
    description: m.description, category: m.category || null, quantity: m.quantity === '' ? null : Number(m.quantity),
    unit_cost: m.unit_cost === '' ? null : Number(m.unit_cost), purchase_date: m.purchase_date || null,
    vendor: m.vendor || null, notes: m.notes || null, receipt_photo_id: m.receipt_photo_id, material_photo_id: m.material_photo_id,
  }));

  const act = async (path, body, msg) => {
    setBusy(true);
    try { await axios.post(`${API}/work-orders/${woId}/${path}`, body || {}, H); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const saveFieldData = async (patch) => {
    try { await axios.patch(`${API}/work-orders/${woId}/field-data`, patch, H); } catch (e) { /* noop */ }
  };

  if (!wo) return <div className="py-16 text-center text-slate-400">{tr(lang, 'Loading…', 'Cargando…')}</div>;
  const s = wo.status;
  const done = ['Awaiting Administrative Closeout', 'Completed', 'Closed by Admin'].includes(s);

  return (
    <div className="pb-24" data-testid="field-wo-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-600 mb-3" data-testid="field-detail-back"><ArrowLeft size={16} /> {tr(lang, 'Back', 'Atrás')}</button>

      {/* Job header */}
      <div className="rounded-xl bg-white shadow-sm border p-4 mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-xs font-bold text-[#0E2A47]" data-testid="field-wo-number">{wo.tfs_work_order_number}</span>
          <PriorityTag p={wo.priority_level} />
        </div>
        <h1 className="text-lg font-bold text-[#0E2A47]" data-testid="field-wo-title">{humanizeDates(wo.job_name)}</h1>
        <div className="mt-1"><StatusChip s={s} lang={lang} /></div>
        <div className="mt-3">
          <div className="text-[11px] uppercase font-bold text-slate-400">{tr(lang, 'Approved Work Scope', 'Alcance de trabajo aprobado')}</div>
          <p className="text-sm text-slate-600">{wo.description_of_work}</p>
        </div>
        {wo.special_instructions && (
          <div className="mt-2" data-testid="field-special-instructions">
            <div className="text-[11px] uppercase font-bold text-slate-400">{tr(lang, 'Special Instructions', 'Instrucciones especiales')}</div>
            <p className="text-sm text-slate-600">{wo.special_instructions}</p>
          </div>
        )}
      </div>

      {/* JOB info */}
      <div className="rounded-xl bg-white border p-4 mb-4 space-y-2 text-sm">
        <div className="font-bold text-slate-500 uppercase text-xs">{tr(lang, 'Job', 'Trabajo')}</div>
        <div className="flex gap-2"><MapPin size={15} className="text-slate-400 mt-0.5" /><div><div className="font-medium">{wo.customer_name}</div><div className="text-slate-500">{wo.service_location}{wo.area_name ? ` · ${wo.area_name}` : ''}</div>{wo.service_address && <div className="text-slate-500">{wo.service_address}</div>}</div></div>
        <div className="flex gap-2"><Clock size={15} className="text-slate-400 mt-0.5" /><div><div className="text-slate-500 text-xs">{tr(lang, 'Scheduled', 'Programado')}</div>{wo.scheduled_date ? `${fmt(wo.scheduled_date)} ${wo.service_window || ''}` : '—'}</div></div>
        <div className="flex gap-2"><Flag size={15} className="text-slate-400 mt-0.5" /><div><div className="text-slate-500 text-xs">{tr(lang, 'Required completion', 'Finalización requerida')}</div>{fmt(wo.required_completion_date || wo.required_service_date)}</div></div>
      </div>

      {/* SITE info */}
      <div className="rounded-xl bg-white border p-4 mb-4 space-y-2 text-sm" data-testid="field-site-info">
        <div className="font-bold text-slate-500 uppercase text-xs">{tr(lang, 'Site', 'Sitio')}</div>
        <div className="flex gap-2"><User size={15} className="text-slate-400 mt-0.5" /><div><div className="text-slate-500 text-xs">{tr(lang, 'On-site contact', 'Contacto en sitio')}</div>{wo.requestor_name || '—'}</div></div>
        <div className="flex gap-2"><Phone size={15} className="text-slate-400 mt-0.5" /><div><div className="text-slate-500 text-xs">{tr(lang, 'Phone', 'Teléfono')}</div>{wo.requestor_phone || wo.requestor_email || '—'}</div></div>
      </div>

      {/* STATUS FLOW actions */}
      {!done && (
        <div className="space-y-3">
          {!wo.accepted_at && (
            <Button className="w-full bg-[#0E2A47]" disabled={busy} onClick={() => act('accept', {}, tr(lang, 'Assignment accepted', 'Asignación aceptada'))} data-testid="field-accept-btn">
              <CheckCircle2 size={18} className="mr-2" /> {tr(lang, 'Accept Assignment', 'Aceptar asignación')}
            </Button>
          )}
          {wo.accepted_at && !wo.en_route_at && (
            <Button className="w-full bg-blue-600" disabled={busy} onClick={() => act('en-route', {}, tr(lang, 'En route', 'En camino'))} data-testid="field-enroute-btn">
              <Truck size={18} className="mr-2" /> {tr(lang, 'En Route', 'En camino')}
            </Button>
          )}
          {wo.en_route_at && !wo.arrived_at && (
            <Button className="w-full bg-blue-700" disabled={busy} onClick={() => act('arrive', {}, tr(lang, 'Arrived', 'Llegó'))} data-testid="field-arrive-btn">
              <MapPin size={18} className="mr-2" /> {tr(lang, 'Arrived', 'Llegué')}
            </Button>
          )}
          {wo.arrived_at && !wo.work_started_at && (
            <Button className="w-full bg-green-600" disabled={busy} onClick={() => act('start', {}, tr(lang, 'Work started', 'Trabajo iniciado'))} data-testid="field-start-btn">
              <PlayCircle size={18} className="mr-2" /> {tr(lang, 'Start Work', 'Iniciar trabajo')}
            </Button>
          )}

          {/* Execution (after start) */}
          {wo.work_started_at && (
            <div className="rounded-xl bg-white border p-4 space-y-4" data-testid="field-execution">
              <div>
                <Label className="text-xs">{tr(lang, 'Before photos & condition', 'Fotos de antes y condición')}</Label>
                <PhotoRow woId={woId} kind="before" photos={beforePhotos} lang={lang}
                  onChange={(p) => { setBeforePhotos(p); saveFieldData({ before_photos: p }); }} label={tr(lang, 'Add before photo', 'Agregar foto de antes')} />
                <div className="mt-2 flex items-center justify-between">
                  <Label className="text-[11px] text-slate-500">{tr(lang, 'Initial condition notes', 'Notas de condición inicial')}</Label>
                  <MicButton lang={lang} testid="field-condition-mic" onAppend={(t) => { const v = (conditionNotes ? conditionNotes + ' ' : '') + t; setConditionNotes(v); saveFieldData({ initial_condition_notes: v }); }} />
                </div>
                <Textarea className="mt-1" rows={2} placeholder={tr(lang, 'Initial condition notes', 'Notas de condición inicial')}
                  value={conditionNotes} data-testid="field-condition-notes"
                  onChange={(e) => setConditionNotes(e.target.value)}
                  onBlur={(e) => saveFieldData({ initial_condition_notes: e.target.value })} />
              </div>

              <Button variant="outline" className="w-full border-purple-300 text-purple-700"
                onClick={() => setShowAuth((v) => !v)} data-testid="field-auth-toggle">
                <AlertTriangle size={16} className="mr-2" /> {tr(lang, 'Additional Work / Authorization Required', 'Trabajo adicional / Autorización requerida')}
              </Button>
              {showAuth && (
                <div className="space-y-2 rounded-md bg-purple-50 p-3">
                  <Textarea rows={2} placeholder={tr(lang, 'Condition discovered *', 'Condición descubierta *')} value={auth.condition}
                    onChange={(e) => setAuth({ ...auth, condition: e.target.value })} data-testid="field-auth-condition" />
                  <Textarea rows={2} placeholder={tr(lang, 'Recommended additional work', 'Trabajo adicional recomendado')} value={auth.recommended}
                    onChange={(e) => setAuth({ ...auth, recommended: e.target.value })} />
                  <Input placeholder={tr(lang, 'Estimated info (optional)', 'Información estimada (opcional)')} value={auth.estimate}
                    onChange={(e) => setAuth({ ...auth, estimate: e.target.value })} />
                  <Button className="w-full bg-purple-600" disabled={busy || !auth.condition}
                    onClick={() => act('authorization', auth, tr(lang, 'Authorization requested', 'Autorización solicitada'))} data-testid="field-auth-submit">
                    {tr(lang, 'Request Authorization', 'Solicitar autorización')}
                  </Button>
                </div>
              )}

              {!showComplete ? (
                <Button className="w-full bg-[#8FC93A] text-[#0E2A47] font-semibold" onClick={() => setShowComplete(true)} data-testid="field-complete-open">
                  {tr(lang, 'Complete Service Visit', 'Completar visita de servicio')}
                </Button>
              ) : (
                <div className="space-y-3 rounded-md bg-slate-50 p-3" data-testid="field-complete-form">
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{tr(lang, 'Work performed *', 'Trabajo realizado *')}</Label>
                      <MicButton lang={lang} testid="field-work-performed-mic" onAppend={(t) => setWork((w) => { const v = (w.work_performed ? w.work_performed + ' ' : '') + t; saveFieldData({ work_performed: v }); return { ...w, work_performed: v }; })} />
                    </div>
                    <Textarea rows={3} value={work.work_performed} onChange={(e) => setWork({ ...work, work_performed: e.target.value })} onBlur={(e) => saveFieldData({ work_performed: e.target.value })} data-testid="field-work-performed" />
                    <p className="mt-1 text-[11px] text-slate-400">{tr(lang, 'Write in English or Spanish — your original words are preserved; management can translate for the client report.', 'Escriba en inglés o español — sus palabras originales se conservan; la gerencia puede traducir para el informe del cliente.')}</p>
                  </div>
                  <div>
                    <Label className="text-xs">{tr(lang, 'Completion status *', 'Estado de finalización *')}</Label>
                    <Select value={work.completion_status} onValueChange={(v) => setWork({ ...work, completion_status: v })}>
                      <SelectTrigger data-testid="field-completion-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {COMPLETION_STATUSES.map((x) => <SelectItem key={x.v} value={x.v}>{tr(lang, x.en, x.es)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {work.completion_status === 'Follow-Up Required' && (
                    <Textarea rows={2} placeholder={tr(lang, 'Follow-up explanation *', 'Explicación de seguimiento *')} value={work.follow_up_explanation}
                      onChange={(e) => setWork({ ...work, follow_up_explanation: e.target.value })} data-testid="field-followup" />
                  )}
                  <div>
                    <Label className="text-xs">{tr(lang, 'After photos', 'Fotos de después')}</Label>
                    <PhotoRow woId={woId} kind="after" photos={afterPhotos} lang={lang}
                      onChange={(p) => { setAfterPhotos(p); saveFieldData({ after_photos: p }); }} label={tr(lang, 'Add after photo', 'Agregar foto de después')} />
                  </div>
                  <div className="border-t pt-3" data-testid="field-materials">
                    <Label className="text-xs">{tr(lang, 'Materials & Supplies', 'Materiales y suministros')}</Label>
                    <label className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={matStatus === 'none_used'} data-testid="field-mat-none"
                        onChange={(e) => setMatStatus(e.target.checked ? 'none_used' : (materials.length ? 'entered' : ''))} />
                      {tr(lang, 'No materials used', 'No se usaron materiales')}
                    </label>
                    {matStatus !== 'none_used' && (
                      <div className="mt-2 space-y-2">
                        {materials.map((m, i) => {
                          const lt = (m.quantity !== '' && m.unit_cost !== '' && !isNaN(Number(m.quantity)) && !isNaN(Number(m.unit_cost))) ? (Number(m.quantity) * Number(m.unit_cost)) : null;
                          return (
                            <div key={i} className="rounded-md border border-slate-200 p-2 space-y-2" data-testid={`field-mat-${i}`}>
                              <div className="flex gap-2">
                                <Input placeholder={tr(lang, 'Description', 'Descripción')} value={m.description} onChange={(e) => updMat(i, 'description', e.target.value)} data-testid={`field-mat-desc-${i}`} />
                                <button type="button" className="text-red-500 text-xs shrink-0" onClick={() => rmMat(i)}>{tr(lang, 'Remove', 'Quitar')}</button>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <Input type="number" step="0.01" placeholder={tr(lang, 'Qty', 'Cant')} value={m.quantity} onChange={(e) => updMat(i, 'quantity', e.target.value)} data-testid={`field-mat-qty-${i}`} />
                                <Input type="number" step="0.01" placeholder={tr(lang, 'Unit $', 'Unidad $')} value={m.unit_cost} onChange={(e) => updMat(i, 'unit_cost', e.target.value)} data-testid={`field-mat-unit-${i}`} />
                                <div className="flex items-center justify-end text-xs text-slate-500">= {lt != null ? `$${lt.toFixed(2)}` : '—'}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <Input placeholder={tr(lang, 'Vendor / Store', 'Proveedor / Tienda')} value={m.vendor} onChange={(e) => updMat(i, 'vendor', e.target.value)} />
                                <Input type="date" value={m.purchase_date} onChange={(e) => updMat(i, 'purchase_date', e.target.value)} />
                              </div>
                              <Input placeholder={tr(lang, 'Category (e.g. HVAC, Plumbing, Electrical)', 'Categoría (p. ej. HVAC, Plomería, Eléctrico)')} value={m.category} onChange={(e) => updMat(i, 'category', e.target.value)} data-testid={`field-mat-cat-${i}`} />
                              <Input placeholder={tr(lang, 'Notes', 'Notas')} value={m.notes} onChange={(e) => updMat(i, 'notes', e.target.value)} />
                              <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
                                <label className="flex items-center gap-1">{tr(lang, 'Receipt', 'Recibo')}<input type="file" accept="image/*" onChange={(e) => matPhoto(i, 'receipt', e.target.files?.[0])} />{m.receipt_photo_id && <span className="text-emerald-600">✓</span>}</label>
                                <label className="flex items-center gap-1">{tr(lang, 'Material', 'Material')}<input type="file" accept="image/*" onChange={(e) => matPhoto(i, 'material', e.target.files?.[0])} />{m.material_photo_id && <span className="text-emerald-600">✓</span>}</label>
                              </div>
                            </div>
                          );
                        })}
                        <button type="button" onClick={addMat} data-testid="field-mat-add"
                          className="w-full rounded-md border border-dashed border-slate-300 py-2 text-sm text-slate-500">+ {tr(lang, 'Add material', 'Agregar material')}</button>
                      </div>
                    )}
                  </div>
                  <div className="border-t pt-3">
                    <Label className="text-xs">{tr(lang, 'On-site representative', 'Representante en sitio')}</Label>
                    {!work.no_rep_available && (
                      <>
                        <div className="mt-1">
                          <Label className="text-[11px] text-slate-500">{tr(lang, 'Verification method', 'Método de verificación')}</Label>
                          <Select value={work.verification_method} onValueChange={(v) => setWork({ ...work, verification_method: v })}>
                            <SelectTrigger data-testid="field-verify-method"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {VERIFY_METHODS.map((x) => <SelectItem key={x.v} value={x.v}>{tr(lang, x.en, x.es)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input className="mt-2" placeholder={tr(lang, 'Representative name', 'Nombre del representante')} value={work.rep_name}
                          onChange={(e) => setWork({ ...work, rep_name: e.target.value })} data-testid="field-rep-name" />
                        <Input className="mt-2" placeholder={tr(lang, 'Title / role', 'Título / rol')} value={work.rep_title}
                          onChange={(e) => setWork({ ...work, rep_title: e.target.value })} />
                        <Input className="mt-2" placeholder={tr(lang, 'Company / customer', 'Empresa / cliente')} value={work.rep_company}
                          onChange={(e) => setWork({ ...work, rep_company: e.target.value })} data-testid="field-rep-company" />
                        {work.verification_method === 'customer_signature' && (
                          <>
                            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500"><PenLine size={13} /> {tr(lang, 'Signature', 'Firma')}</div>
                            <SignaturePad lang={lang} onChange={(sig) => setWork({ ...work, rep_signature: sig })} />
                          </>
                        )}
                      </>
                    )}
                    <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={work.no_rep_available} data-testid="field-no-rep"
                        onChange={(e) => setWork({ ...work, no_rep_available: e.target.checked })} />
                      {tr(lang, 'No representative available', 'No hay representante disponible')}
                    </label>
                  </div>
                  <div className="border-t pt-3" data-testid="field-feedback">
                    <Label className="text-xs">{tr(lang, 'On-site feedback (optional)', 'Comentarios en sitio (opcional)')}</Label>
                    {[
                      { k: 'feedback_courtesy', en: 'Technician Courtesy', es: 'Cortesía del técnico' },
                      { k: 'feedback_communication', en: 'Communication / Informative', es: 'Comunicación / Informativo' },
                      { k: 'feedback_resolution', en: 'Issue Resolution', es: 'Resolución del problema' },
                    ].map((row) => (
                      <div key={row.k} className="flex items-center justify-between mt-2">
                        <span className="text-xs text-slate-600">{tr(lang, row.en, row.es)}</span>
                        <div className="flex gap-1" data-testid={`rating-${row.k}`}>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button" onClick={() => setWork({ ...work, [row.k]: n })}
                              className={`h-7 w-7 rounded-full text-xs font-semibold ${work[row.k] >= n ? 'bg-[#8FC93A] text-[#0E2A47]' : 'bg-slate-100 text-slate-400'}`}>{n}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="mt-2 flex items-center justify-end">
                      <MicButton lang={lang} testid="field-feedback-mic" onAppend={(t) => setWork((w) => { const v = (w.feedback_comments ? w.feedback_comments + ' ' : '') + t; saveFieldData({ feedback_comments: v }); return { ...w, feedback_comments: v }; })} />
                    </div>
                    <Textarea className="mt-1" rows={2} placeholder={tr(lang, 'Optional comments', 'Comentarios opcionales')} value={work.feedback_comments}
                      onChange={(e) => setWork({ ...work, feedback_comments: e.target.value })} onBlur={(e) => saveFieldData({ feedback_comments: e.target.value })} data-testid="field-feedback-comments" />
                  </div>
                  <Button className="w-full bg-[#0E2A47]" disabled={busy || !work.work_performed}
                    onClick={() => act('complete', { ...work, before_photos: beforePhotos, after_photos: afterPhotos, materials: cleanMats(), materials_status: matStatus === 'none_used' ? 'none_used' : (cleanMats().length ? 'entered' : null) }, tr(lang, 'Service report submitted', 'Informe de servicio enviado'))}
                    data-testid="field-submit-report">
                    {tr(lang, 'Submit Field Service Report', 'Enviar informe de servicio de campo')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {done && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center text-green-800" data-testid="field-done-banner">
          <CheckCircle2 className="mx-auto mb-2" />
          {tr(lang,
            'Service report submitted. Now Awaiting Administrative Closeout.',
            'Informe de servicio enviado. Ahora esperando el cierre administrativo.')}
        </div>
      )}
    </div>
  );
};

const FieldWorkOrders = () => {
  const { token, user, logout } = useContext(AuthContext);
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState('active');
  const [wos, setWos] = useState([]);
  const [sel, setSel] = useState(null);
  const [lang, setLang] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('field_lang')) || 'en');
  const setLanguage = (l) => { try { localStorage.setItem('field_lang', l); } catch { /* noop */ } setLang(l); };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/work-orders`, H); setWos(r.data || []); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = wos.filter((w) => !ACTIVE_DONE.includes(w.status));
  const completed = wos.filter((w) => ACTIVE_DONE.includes(w.status));
  const list = tab === 'completed' ? completed : active;

  const LangToggle = () => (
    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs" data-testid="field-lang-toggle">
      <button onClick={() => setLanguage('en')} data-testid="field-lang-en"
        className={`px-2.5 py-1 flex items-center gap-1 ${lang === 'en' ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-500'}`}>
        <Languages size={12} /> EN
      </button>
      <button onClick={() => setLanguage('es')} data-testid="field-lang-es"
        className={`px-2.5 py-1 ${lang === 'es' ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-500'}`}>
        ES
      </button>
    </div>
  );

  if (sel) return (
    <div className="max-w-lg mx-auto px-4 py-4">
      <div className="flex justify-end mb-2"><LangToggle /></div>
      <FieldDetail woId={sel} lang={lang} onBack={() => { setSel(null); load(); }} onChanged={load} />
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-4 pb-24" data-testid="field-work-orders">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <h1 className="text-xl font-bold text-[#0E2A47]">{tr(lang, 'My Work Orders', 'Mis órdenes de trabajo')}</h1>
          <p className="text-sm text-slate-500">{tr(lang, 'Assigned work for', 'Trabajo asignado a')} {user?.name}</p>
        </div>
        <LangToggle />
      </div>

      {tab === 'profile' ? (
        <div className="rounded-xl bg-white border p-5 text-center mt-4" data-testid="field-profile">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#0E2A47] text-white flex items-center justify-center mb-3"><User /></div>
          <div className="font-bold text-[#0E2A47]">{user?.name}</div>
          <div className="text-sm text-slate-500">{user?.email}</div>
          <div className="text-xs text-[#8FC93A] font-semibold mt-1">{tr(lang, 'Field Personnel', 'Personal de campo')}</div>
          <Button variant="outline" className="mt-4" onClick={logout} data-testid="field-logout"><LogOut size={15} className="mr-2" /> {tr(lang, 'Logout', 'Cerrar sesión')}</Button>
        </div>
      ) : (
        <div className="space-y-3 mt-4">
          {list.length === 0 && <div className="py-16 text-center text-slate-400 text-sm">{tr(lang, `No ${tab} work orders.`, `No hay órdenes ${tab === 'completed' ? 'completadas' : 'activas'}.`)}</div>}
          {list.map((w) => (
            <button key={w.id} onClick={() => setSel(w.id)} data-testid={`field-wo-card-${w.tfs_work_order_number}`}
              className="w-full text-left rounded-xl bg-white border p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-xs font-bold text-[#0E2A47]">{w.tfs_work_order_number}</span>
                <PriorityTag p={w.priority_level} />
              </div>
              <div className="font-semibold text-slate-800">{humanizeDates(w.job_name)}</div>
              <div className="text-sm text-slate-500">{w.customer_name} · {w.service_location}</div>
              <div className="mt-2 flex items-center justify-between">
                <StatusChip s={w.status} lang={lang} />
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  {w.scheduled_date ? fmt(w.scheduled_date) : tr(lang, 'Unscheduled', 'Sin programar')} <ChevronRight size={14} />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t flex justify-around py-2 z-40" data-testid="field-bottom-nav">
        {[
          { k: 'active', en: 'My Work', es: 'Mi trabajo', icon: Briefcase },
          { k: 'completed', en: 'Completed', es: 'Completadas', icon: CheckCircle2 },
          { k: 'profile', en: 'Profile', es: 'Perfil', icon: User },
        ].map((n) => {
          const Icon = n.icon;
          return (
            <button key={n.k} onClick={() => { setSel(null); setTab(n.k); }} data-testid={`field-nav-${n.k}`}
              className={`flex flex-col items-center gap-0.5 px-4 py-1 text-xs ${tab === n.k ? 'text-[#0E2A47] font-semibold' : 'text-slate-400'}`}>
              <Icon size={20} /> {tr(lang, n.en, n.es)}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default FieldWorkOrders;
